import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { DndContext, closestCenter, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CopyIcon, LockIcon, PlusIcon } from 'lucide-react';
import { STORY_CARDS_MAX, STORY_CARD_TYPES, type StoryCard, type StoryCardType } from '@org/model';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  ConfirmDialog,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  ExpandableSortableItem,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  STORY_CARD_REGISTRY,
  TextArea,
  createStoryCard,
} from '@org/ui';
import { useDndSensors } from '../shared/use-dnd-sensors';
import { StoryCardForm } from './story-card-forms';
import {
  cardSummary,
  cardsToJson,
  duplicateCard,
  parseCardsJson,
  type CardsJsonResult,
} from './woman-editor-helpers';

type StoryCardsEditorProps = {
  cards: StoryCard[];
  onChange: (cards: StoryCard[]) => void;
  /** Story page the admin is working on: 0 = opening, card i = i + 1, last = closing. */
  onShowPage: (pageIndex: number) => void;
};

type CardItem = {
  id: string;
  name: string;
  description: string;
  card: StoryCard;
  index: number;
};

type JsonError = Exclude<CardsJsonResult, { ok: true }>;

/** Tells the preview which card is being edited as soon as its form opens. */
function ShowOnOpen({ onShow, children }: { onShow: () => void; children: React.ReactNode }) {
  useEffect(() => {
    onShow();
    // Only when the form opens; typing keeps the preview where it is.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return <div onFocusCapture={onShow}>{children}</div>;
}

function FixedRow({ title, description, onClick }: { title: string; description: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="mb-3 flex w-full items-center gap-3 rounded-lg border border-dashed border-border bg-muted/40 p-3 text-left"
    >
      <LockIcon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      <span className="flex min-w-0 flex-col">
        <span className="font-medium">{title}</span>
        <span className="text-xs text-muted-foreground">{description}</span>
      </span>
    </button>
  );
}

export function StoryCardsEditor({ cards, onChange, onShowPage }: StoryCardsEditorProps) {
  const { t, i18n } = useTranslation('admin');
  const uiLang = i18n.language?.startsWith('pt') ? 'pt' : 'en';
  const sensors = useDndSensors();

  const [tab, setTab] = useState<'visual' | 'json'>('visual');
  const [jsonText, setJsonText] = useState('');
  const [jsonError, setJsonError] = useState<JsonError | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [lastAddedId, setLastAddedId] = useState<string | null>(null);

  const isFull = cards.length >= STORY_CARDS_MAX;

  // Card type names come from the story labels ("Text", "Video"…), in the admin's language.
  const typeLabel = (type: string) => {
    const entry = (STORY_CARD_REGISTRY as Record<string, { labelKey: string } | undefined>)[type];
    return entry ? t(`women.storyLabels.${entry.labelKey}`) : type;
  };

  const items = useMemo<CardItem[]>(
    () =>
      cards.map((card, index) => ({
        id: card.id,
        name: t('women.cards.itemTitle', { n: index + 1, type: typeLabel(card.type) }),
        description: cardSummary(card, uiLang),
        card,
        index,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cards, t, uiLang],
  );

  const handleTabChange = (next: string) => {
    if (next === 'json') {
      // Always show what the Visual tab currently holds.
      setJsonText(cardsToJson(cards));
      setJsonError(null);
    }
    setTab(next === 'json' ? 'json' : 'visual');
  };

  // The cards were replaced from outside (saved, AI draft loaded) while the JSON tab is open.
  useEffect(() => {
    if (tab === 'json') {
      setJsonText(cardsToJson(cards));
      setJsonError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cards]);

  const updateCard = (id: string, next: StoryCard) =>
    onChange(cards.map((card) => (card.id === id ? next : card)));

  const addCard = (type: StoryCardType) => {
    if (isFull) return;
    const card = createStoryCard(type);
    setLastAddedId(card.id);
    onChange([...cards, card]);
    onShowPage(cards.length + 1);
  };

  const duplicate = (index: number) => {
    if (isFull) return;
    const copy = duplicateCard(cards[index]);
    setLastAddedId(copy.id);
    onChange([...cards.slice(0, index + 1), copy, ...cards.slice(index + 1)]);
    onShowPage(index + 2);
  };

  const confirmDelete = () => {
    if (!pendingDeleteId) return;
    onChange(cards.filter((card) => card.id !== pendingDeleteId));
    setPendingDeleteId(null);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = cards.findIndex((card) => card.id === active.id);
    const to = cards.findIndex((card) => card.id === over.id);
    if (from < 0 || to < 0) return;
    onChange(arrayMove(cards, from, to));
    onShowPage(to + 1);
  };

  const applyJson = () => {
    const result = parseCardsJson(jsonText);
    if (!result.ok) {
      setJsonError(result);
      return;
    }
    setJsonError(null);
    onChange(result.cards);
    setJsonText(cardsToJson(result.cards));
    toast.success(t('women.cards.json.applied', { count: result.cards.length }));
  };

  const copyJson = async () => {
    try {
      await navigator.clipboard.writeText(jsonText);
      toast.success(t('women.cards.json.copied'));
    } catch {
      toast.error(t('women.cards.json.copyFailed'));
    }
  };

  const pendingDeleteItem = items.find((item) => item.id === pendingDeleteId) ?? null;

  return (
    <Tabs value={tab} onValueChange={handleTabChange}>
      <TabsList>
        <TabsTrigger value="visual">{t('women.cards.tabs.visual')}</TabsTrigger>
        <TabsTrigger value="json">{t('women.cards.tabs.json')}</TabsTrigger>
      </TabsList>

      <TabsContent value="visual" className="mt-4">
        <FixedRow
          title={t('women.cards.fixed.openingTitle')}
          description={t('women.cards.fixed.openingDescription')}
          onClick={() => onShowPage(0)}
        />

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={items.map((item) => item.id)} strategy={verticalListSortingStrategy}>
            {items.map((item) => (
              <ExpandableSortableItem
                key={item.id}
                item={item}
                onUpdate={() => undefined}
                onDelete={() => setPendingDeleteId(item.id)}
                defaultExpanded={item.id === lastAddedId}
                renderActions={() => (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-1"
                    disabled={isFull}
                    title={t('women.cards.duplicate')}
                    aria-label={t('women.cards.duplicate')}
                    onClick={() => duplicate(item.index)}
                  >
                    <CopyIcon className="h-4 w-4" />
                  </Button>
                )}
                renderContent={() => (
                  <ShowOnOpen onShow={() => onShowPage(item.index + 1)}>
                    <StoryCardForm card={item.card} onChange={(next) => updateCard(item.id, next)} />
                  </ShowOnOpen>
                )}
              />
            ))}
          </SortableContext>
        </DndContext>

        {cards.length === 0 ? (
          <p className="mb-3 rounded-lg border border-dashed border-border p-4 text-center text-sm text-muted-foreground">
            {t('women.cards.empty')}
          </p>
        ) : null}

        <div className="mb-3 flex flex-wrap items-center gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button type="button" variant="outline" disabled={isFull}>
                <PlusIcon className="mr-1 h-4 w-4" />
                {t('women.cards.add')}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {STORY_CARD_TYPES.map((type) => (
                <DropdownMenuItem key={type} onSelect={() => addCard(type)}>
                  <span className="flex flex-col">
                    <span className="font-medium">{typeLabel(type)}</span>
                    <span className="text-xs text-muted-foreground">{t(`women.cards.typeHints.${type}`)}</span>
                  </span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <span className="text-xs text-muted-foreground">
            {isFull
              ? t('women.cards.maxReached', { max: STORY_CARDS_MAX })
              : t('women.cards.count', { count: cards.length, max: STORY_CARDS_MAX })}
          </span>
        </div>

        <FixedRow
          title={t('women.cards.fixed.closingTitle')}
          description={t('women.cards.fixed.closingDescription')}
          onClick={() => onShowPage(cards.length + 1)}
        />
      </TabsContent>

      <TabsContent value="json" className="mt-4">
        <p className="mb-2 text-sm text-muted-foreground">{t('women.cards.json.hint')}</p>
        <TextArea
          value={jsonText}
          onChange={(event) => setJsonText(event.target.value)}
          rows={24}
          spellCheck={false}
          aria-label={t('women.cards.tabs.json')}
          className="font-mono text-xs leading-5"
        />
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button type="button" onClick={applyJson}>
            {t('women.cards.json.apply')}
          </Button>
          <Button type="button" variant="outline" onClick={copyJson}>
            <CopyIcon className="mr-1 h-4 w-4" />
            {t('women.cards.json.copy')}
          </Button>
        </div>
        {jsonError ? (
          <Alert variant="destructive" className="mt-3">
            <AlertTitle>{t('women.cards.json.errorTitle')}</AlertTitle>
            <AlertDescription>
              {jsonError.reason === 'syntax' ? (
                <p>{t('women.cards.json.syntaxError', { message: jsonError.message })}</p>
              ) : jsonError.reason === 'not-a-list' ? (
                <p>{t('women.cards.json.notAList')}</p>
              ) : (
                <ul className="list-disc space-y-1 pl-5">
                  {jsonError.issues.map((issue, index) => (
                    <li key={index}>
                      {issue.tooMany
                        ? t('women.cards.json.tooMany', { max: STORY_CARDS_MAX })
                        : issue.card === null
                          ? t('women.cards.json.issueList', { message: issue.message })
                          : issue.field
                            ? t('women.cards.json.issueField', {
                                n: issue.card,
                                field: issue.field,
                                message: issue.message,
                              })
                            : t('women.cards.json.issueCard', { n: issue.card, message: issue.message })}
                    </li>
                  ))}
                </ul>
              )}
            </AlertDescription>
          </Alert>
        ) : null}
      </TabsContent>

      <ConfirmDialog
        open={pendingDeleteId !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDeleteId(null);
        }}
        description={t('women.cards.deleteConfirm', { name: pendingDeleteItem?.name ?? '' })}
        confirmLabel={t('women.cards.delete')}
        destructive
        onConfirm={confirmDelete}
      />
    </Tabs>
  );
}
